// Daily standup — structured flow with AI summary + action items.
//
// Phases:
//   idle      → Textarea for user's daily update
//   analyzing → AI reads update + ticket data, generates summary + actions
//   review    → Summary card + action cards with Confirm / Skip
//   complete  → "Saved. Thank you for today's update."
//
// Actions are NEVER auto-executed. Each proposed change (create ticket,
// move ticket, update ticket) gets a preview card. User confirms or skips
// each one individually. Once all are resolved the summary is persisted
// as a `standup_summary` note row and the phase flips to `complete`.

import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../../shared/functions/supabaseClient.js'
import { getSDK } from '../../../shared/functions/sdk.js'

// Credit metering for standup AI now happens server-side inside the
// ai-chat edge function — see server/packages/credit-meter.

const AI_URL = `${SUPABASE_URL}/functions/v1/ai-chat`
// Local date (not UTC) — avoids day mismatch when user is ahead of UTC
const _today = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const _streamAnalysis = async (updateText, onChunk, onDone) => {
  const prompt = `[DAILY STANDUP MODE] My update: ${updateText}

Write a standup summary in FIRST PERSON as if I wrote it myself. Example: "Worked on the API fix and got it close to done. Updated the KB article. Blocked on QA feedback for auth changes."

Rules:
1. Write 2-3 sentences in first person ("I worked on...", "Fixed...", "Blocked on...") — NOT as an AI ("I have noted...", "I will keep an eye...")
2. No headers, no bullets, no "Follow-ups" section
3. Add ACTION tags for ticket changes. Each ACTION MUST include:
   - "summary": WHY this change (e.g. "Reported finishing this work")
   - "change": what exactly changes (e.g. "Status: Backlog → In Review")
   - "followUps": 2 suggested next questions
4. For move_ticket: include "from" and "to" column labels
5. For update_ticket: include "field" and "value"
6. For create_ticket: include "title" and "body"
7. If no actions: <!--ACTION:{"type":"info","followUps":["question1","question2"]}-->`

  try {
    const res = await fetch(AI_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        messages: [{ role: 'user', content: prompt }]
      })
    })

    if (!res.ok || !res.body) {
      const errText = await res.text().catch(() => '')
      console.error('[Standup AI]', res.status, errText.slice(0, 200))
      onDone('AI analysis failed. Try again.')
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let full = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const json = line.slice(6).trim()
        if (json === '[DONE]') continue
        try {
          const parsed = JSON.parse(json)
          if (parsed.text) { full += parsed.text; onChunk(full) }
        } catch {}
      }
    }
    onDone(full || 'No response from AI.')
  } catch (e) {
    console.error('[Standup] stream error:', e)
    onDone('Connection error — please try again.')
  }
}
const _parseResponse = (text) => {
  const actions = []
  const followUps = []
  const re = /<!--ACTION:(.*?)-->/g
  let m
  while ((m = re.exec(text)) !== null) {
    try {
      const data = JSON.parse(m[1])
      if (data.followUps) followUps.push(...data.followUps)
      if (data.type && data.type !== 'info') {
        actions.push({ ...data, _status: 'pending', _id: Math.random().toString(36).slice(2) })
      }
    } catch {}
  }
  const display = text
    .replace(/<!--ACTION:.*?-->/g, '')
    .replace(/\*\*Summary\*\*/gi, '')
    .replace(/\*\*Suggested Actions\*\*/gi, '')
    .replace(/\*\*Today'?s Summary\*\*/gi, '')
    .replace(/\*\*Follow-ups?\*\*/gi, '')
    .replace(/\*\*/g, '')
    .replace(/^- .*follow.up.*$/gim, '')
    .replace(/^- Would you like.*$/gim, '')
    .replace(/^- Do you need.*$/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return { summary: display, actions, followUps: [...new Set(followUps)] }
}
const _executeAction = async (action, root) => {
  // migrated 2026-04-27 to SDK route workspaceProject.tickets
  const sdk = getSDK()
  const email = root.currentUserEmail

  try {
    if (action.type === 'create_ticket') {
      const result = await sdk.execute('workspaceProject.tickets', 'create', {
        payload: {
          title: action.title || 'New ticket',
          body: action.body || '',
          column_key: action.column_key || 'backlog',
          priority: action.priority || 'medium',
          assignee_email: (action.assignee || email).split(',')[0].trim(),
          reporter_email: email,
          deleted: false,
          updated_at: new Date().toISOString()
        }
      })
      const created = Array.isArray(result?.data) ? result.data[0] : (result?.data || result)
      if (created) {
        root.update({ myTickets: [created, ...(root.myTickets || [])] })
        return `Created #${created.number || created.id}`
      }
    } else if (action.type === 'update_ticket' && action.number) {
      const row = {}
      if (action.body) row.body = action.body
      if (action.column_key) row.column_key = action.column_key
      if (action.priority) row.priority = action.priority
      if (action.title) row.title = action.title
      row.updated_at = new Date().toISOString()
      await sdk.execute('workspaceProject.tickets', 'update', {
        number: action.number,
        payload: row
      })
      root.update({
        myTickets: (root.myTickets || []).map(t =>
          (t.number || t.id) === action.number ? { ...t, ...row } : t
        )
      })
      return `Updated #${action.number}`
    } else if (action.type === 'move_ticket' && action.number) {
      await sdk.execute('workspaceProject.tickets', 'update', {
        number: action.number,
        payload: {
          column_key: action.column_key,
          updated_at: new Date().toISOString()
        }
      })
      root.update({
        myTickets: (root.myTickets || []).map(t =>
          (t.number || t.id) === action.number ? { ...t, column_key: action.column_key } : t
        )
      })
      return `Moved #${action.number}`
    }
    return null
  } catch (e) {
    console.error('[Standup action]', e)
    return null
  }
}

// ─── Persist completed standup ────────────────────────────────────────────

// Upserts on (author, date) so re-submits overwrite the row instead of
// stacking duplicates. Backed by the unique index in migration 0033.
const _saveStandup = async (root, userUpdate, summary, actions) => {
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.standups upsert
    await getSDK().execute('workspaceProject.standups', 'upsert', {
      payload: {
        author: root.currentUserEmail,
        date: _today(),
        update_text: userUpdate,
        summary,
        actions: actions.map(a => ({
          type: a.type,
          title: a.title,
          number: a.number,
          summary: a.summary,
          status: a._status,
          result: a._result
        }))
      }
    })
  } catch (e) { console.error('[Standup save]', e) }
}
const _submitUpdate = async (root, text) => {
  if (!text?.trim()) return
  root.update({
    _standupPhase: 'analyzing',
    _dailyDraft: text,
    _standupSummary: '',
    _standupActions: []
  })

  await _streamAnalysis(
    text,
    (partial) => {
      const { summary } = _parseResponse(partial)
      root.update({ _standupSummary: summary })
    },
    (final) => {
      const { summary, actions, followUps } = _parseResponse(final)
      const hasActions = actions.length > 0
      root.update({
        _standupPhase: hasActions ? 'review' : 'complete',
        _standupSummary: summary,
        _standupActions: actions,
        _standupFollowUps: followUps || []
      })
      if (!hasActions) _saveStandup(root, text, summary, [])
    }
  )
}

const _confirmAction = async (actionId, root) => {
  const actions = root._standupActions || []
  const action = actions.find(a => a._id === actionId)
  if (!action) return

  root.update({
    _standupActions: actions.map(a =>
      a._id === actionId ? { ...a, _status: 'executing' } : a
    )
  })

  const result = await _executeAction(action, root)
  const updated = (root._standupActions || []).map(a =>
    a._id === actionId ? { ...a, _status: 'confirmed', _result: result || 'Done' } : a
  )
  root.update({ _standupActions: updated })
  _maybeComplete(root)
}

const _skipAction = (actionId, root) => {
  root.update({
    _standupActions: (root._standupActions || []).map(a =>
      a._id === actionId ? { ...a, _status: 'skipped' } : a
    )
  })
  _maybeComplete(root)
}

const _maybeComplete = (root) => {
  const actions = root._standupActions || []
  if (!actions.length) return
  if (actions.every(a => a._status === 'confirmed' || a._status === 'skipped')) {
    root.update({ _standupPhase: 'complete' })
    _saveStandup(root, root._dailyDraft, root._standupSummary, actions)
  }
}

export const TicketsDailyUpdate = {
  flow: 'y',
  gap: 'Z1',

  HeaderRow: {
    flow: 'x',
    align: 'center space-between',

    HistoryLink: {
      tag: 'button',
      type: 'button',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'blue',
      background: 'transparent',
      borderWidth: '0',
      cursor: 'pointer',
      padding: '0',
      text: 'Previous standups →',
      ':hover': { opacity: '.7' },
      onClick: (e, el) => {
        let root = el
        while (root && !root.routes) root = root.parent
        if (root) el.router('/tickets/standups', root)
      }
    }
  },

  CompletedCard: {
    hide: (el, s) => s.root?._standupPhase !== 'complete',
    flow: 'y',
    gap: 'Z1',
    padding: 'A',
    round: 'A',
    background: 'green.06',
    borderWidth: '1px',
    borderColor: 'green.2',
    borderStyle: 'solid',

    TopRow: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',
      Icon: { name: 'check', color: 'green', boxSize: 'A1' },
      Label: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'green',
        text: 'Standup complete — thank you for today\'s update!'
      }
    },

    Summary: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'title',
      lineHeight: '1.6',
      whiteSpace: 'pre-wrap',
      text: (el, s) => s.root?._standupSummary || ''
    },

    Applied: {
      hide: (el, s) =>
        !(s.root?._standupActions || []).some(a => a._status === 'confirmed'),
      flow: 'y',
      gap: 'Z',
      marginTop: 'Z',

      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'green',
        text: 'Applied changes:'
      },
      List: {
        flow: 'y',
        gap: 'Z',
        childExtends: 'StandupAppliedCard',
        childrenAs: 'state',
        children: (el, s) =>
          (s.root?._standupActions || [])
            .filter(a => a._status === 'confirmed')
            .map(a => ({
              _type: a.type,
              _number: a.number,
              _title: a.title,
              _result: a._result || a.summary,
              _ticketId: a.number
                ? (s.root?.myTickets || []).find(t => (t.number || t.id) === a.number)?.id
                : null
            }))
      }
    },

  },

  ActionRow: {
    hide: (el, s) => s.root?._standupPhase !== 'complete',
    flow: 'x wrap',
    gap: 'Z',

    childExtends: 'StandupFollowUp',
    childrenAs: 'state',
    children: (el, s) => {
      const followUps = (s.root?._standupFollowUps || []).map(text => ({ _label: text }))
      return [...followUps, { _label: '+ Submit another update', _isNew: true }]
    }
  },

  InputSection: {
    hide: (el, s) => (s.root?._standupPhase || 'idle') !== 'idle',
    flow: 'y',
    gap: 'Z1',

    Greeting: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      lineHeight: '1.5',
      text: (el, s) => {
        const me = el.call('findMe', s)
        const name = (me?.name || '').split(' ')[0] || 'there'
        return `Hi ${name} — what did you work on today?`
      }
    },

    InputBox: {
      position: 'relative',
      round: 'A',
      overflow: 'hidden',
      theme: 'field',

      Textarea: {
        tag: 'textarea',
        placeholder: 'What did you work on today?',
        rows: '4',
        resize: 'vertical',
        width: '100%',
        boxSizing: 'border-box',
        padding: 'A A2 C1',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        color: 'title',
        fontSize: 'Z2',
        lineHeight: '1.5',
        fontFamily: 'inherit',
        onCreate: (el, s) => {
          if (s.root?._dailyDraft) el.node.value = s.root._dailyDraft
        },
        onKeyDown: (e, el, s) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            const text = el.node.value?.trim()
            if (text) _submitUpdate(s.root, text)
          }
        }
      },

      SendBtn: {
        extends: 'CircleButton',
        position: 'absolute',
        bottom: 'Z',
        right: 'Z',
        zIndex: '1',
        padding: 'Z',
        icon: 'chevronUp',
        Icon: { transform: 'rotate(45deg)' },
        '@dark': {
          background: 'white',
          color: 'title',
          ':hover': { background: 'gray14', color: 'gray1' },
          ':active': { background: 'gray13', color: 'gray1' }
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          const ta = el.parent?.Textarea?.node
          const draft = ta?.value?.trim()
          if (draft) _submitUpdate(s.root, draft)
        }
      }
    },

    QuickFills: {
      flow: 'x wrap',
      gap: 'Z',
      childExtends: 'StandupQuickFill',
      childrenAs: 'state',
      children: (el, s) => {
        const tickets = s.root?.myTickets || []
        const fills = []

        const active = tickets.filter(t => t.column_key === '4d6ab783')
        for (const t of active.slice(0, 2)) {
          fills.push({
            label: `#${t.number || t.id} progress`,
            _text: `Made progress on #${t.number || t.id} (${t.title}). `
          })
        }

        const review = tickets.filter(t => t.column_key === '821cd85d')
        if (review.length) {
          const t = review[0]
          fills.push({
            label: `#${t.number || t.id} done`,
            _text: `Finished #${t.number || t.id} (${t.title}), ready to close. `
          })
        }

        if (fills.length < 4) {
          const backlog = tickets.filter(t =>
            t.column_key !== '4d6ab783' && t.column_key !== '821cd85d' && t.column_key !== 'eede5560'
          )
          if (backlog.length) {
            const t = backlog[0]
            fills.push({
              label: `Started #${t.number || t.id}`,
              _text: `Started working on #${t.number || t.id} (${t.title}). `
            })
          }
        }

        if (fills.length < 3) fills.push({ label: 'Code review', _text: 'Reviewed PRs and gave feedback. ' })
        if (fills.length < 4) fills.push({ label: 'Blocked', _text: 'Blocked on ' })
        if (fills.length < 5) fills.push({ label: 'Meetings', _text: 'Attended meetings about ' })

        return fills.slice(0, 5)
      }
    }
  },

  AnalyzingCard: {
    hide: (el, s) => s.root?._standupPhase !== 'analyzing',
    flow: 'y',
    gap: 'Z1',
    padding: 'A',
    round: 'A',
    background: 'blue.06',
    borderWidth: '1px',
    borderColor: 'blue.15',
    borderStyle: 'solid',

    TopRow: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',
      Spinner: {
        boxSize: 'A1',
        flexShrink: '0',
        round: '50%',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'blue.2',
        borderTopColor: 'blue',
        animation: 'spin 0.9s linear infinite',
        '@keyframes spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' }
        }
      },
      Label: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'blue',
        text: 'Analyzing your update…'
      }
    },

    Preview: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'title',
      lineHeight: '1.6',
      whiteSpace: 'pre-wrap',
      opacity: '.6',
      text: (el, s) =>
        s.root?._standupSummary || 'Reading your update and comparing with your tickets…'
    }
  },

  ReviewSection: {
    hide: (el, s) => s.root?._standupPhase !== 'review',
    flow: 'y',
    gap: 'A',

    OriginalCard: {
      flow: 'y',
      gap: 'V',
      InputBox: {
        position: 'relative',
        round: 'A',
        overflow: 'hidden',
        theme: 'field',

        Textarea: {
          tag: 'textarea',
          rows: '4',
          resize: 'vertical',
          width: '100%',
          boxSizing: 'border-box',
          padding: 'A A2 C1',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'title',
          fontSize: 'Z2',
          lineHeight: '1.5',
          fontFamily: 'inherit',
          onCreate: (el, s) => {
            if (s.root?._dailyDraft) el.node.value = s.root._dailyDraft
          },
          onKeyDown: (e, el, s) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              const text = el.node.value?.trim()
              if (text && text !== s.root?._dailyDraft) _submitUpdate(s.root, text)
            }
          }
        },

        ResubmitBtn: {
          extends: 'CircleButton',
          position: 'absolute',
          bottom: 'Z',
          right: 'Z',
          zIndex: '1',
          padding: 'Z',
          icon: 'chevronUp',
          Icon: { transform: 'rotate(45deg)' },
          title: 'Re-analyze update',
          onClick: (e, el, s) => {
            e.preventDefault()
            const ta = el.parent?.Textarea?.node
            const draft = ta?.value?.trim()
            if (draft) _submitUpdate(s.root, draft)
          }
        }
      }
    },

    SummaryCard: {
      flow: 'y',
      gap: 'Z1',
      padding: 'A',
      round: 'A',
      background: 'white.06',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',

      Header: {
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        Icon: { name: 'magicstar', color: 'purple', boxSize: 'A1' },
        Label: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'purple',
          text: 'AI-generated summary'
        }
      },

      Body: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'title',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        text: (el, s) => s.root?._standupSummary || ''
      }
    },

    ActionsHeader: {
      hide: (el, s) => !(s.root?._standupActions || []).length,
      flow: 'x',
      align: 'center space-between',

      Label: {
        fontSize: 'Z2',
        fontWeight: '600',
        color: 'title',
        text: (el, s) => {
          const actions = s.root?._standupActions || []
          const pending = actions.filter(a => a._status === 'pending').length
          if (!pending) return 'All actions addressed'
          return `${pending} suggested action${pending === 1 ? '' : 's'} — confirm or skip each:`
        }
      },

      SkipAllBtn: {
        hide: (el, s) =>
          !(s.root?._standupActions || []).some(a => a._status === 'pending'),
        tag: 'button',
        type: 'button',
        fontSize: 'Y2',
        fontWeight: '600',
        padding: 'V Z',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        background: 'transparent',
        color: 'caption',
        text: 'Skip all',
        ':hover': { color: 'title' },
        onClick: (e, el, s) => {
          s.root.update({
            _standupActions: (s.root._standupActions || []).map(a =>
              a._status === 'pending' ? { ...a, _status: 'skipped' } : a
            )
          })
          _maybeComplete(s.root)
        }
      }
    },

    ActionCards: {
      flow: 'y',
      gap: 'Z',
      childExtends: 'StandupActionCard',
      childrenAs: 'state',
      children: (el, s) => s.root?._standupActions || []
    },

    ConfirmAllBtn: {
      hide: (el, s) =>
        !(s.root?._standupActions || []).some(a => a._status === 'pending'),
      tag: 'button',
      type: 'button',
      position: 'sticky',
      bottom: 'Z',
      zIndex: '2',
      padding: 'Z1 B',
      round: 'Z',
      fontSize: 'Z2',
      fontWeight: '700',
      border: 'none',
      cursor: 'pointer',
      background: 'blue',
      color: 'white',
      alignSelf: 'stretch',
      text: 'Confirm All & Save',
      ':hover': { opacity: '.9' },
      onClick: async (e, el, s) => {
        const actions = s.root._standupActions || []
        for (const action of actions) {
          if (action._status === 'pending') {
            await _confirmAction(action._id, s.root)
          }
        }
      }
    }
  },

  onCreate: async (el, s) => {
    const root = s.root
    if (!root) return
    let email = root.currentUserEmail
    if (!email) {
      await new Promise(resolve => {
        let attempts = 0
        const check = setInterval(() => {
          email = root.currentUserEmail
          if (email || ++attempts > 30) { clearInterval(check); resolve() }
        }, 200)
      })
    }
    if (!email) return

    try {
      // migrated 2026-04-27 to SDK route workspaceProject.standups list (single row)
      const result = await getSDK().execute('workspaceProject.standups', 'list', {
        filter: { author: email, date: _today() },
        single: true,
        limit: 1,
        options: { columns: 'summary, actions' }
      })
      const data = Array.isArray(result?.data)
        ? result.data[0]
        : (result?.data ?? result ?? null)

      if (data) {
        const actions = (data.actions || []).map(a => ({
          ...a,
          _status: a.status || 'confirmed',
          _id: Math.random().toString(36).slice(2)
        }))
        root.update({
          _standupPhase: 'complete',
          _standupSummary: data.summary || '',
          _standupActions: actions
        })
      } else {
        root.update({ _standupPhase: 'idle' })
      }
    } catch {}
  }
}

const _actionColor = (s) => {
  if (s._status === 'confirmed') return 'green'
  if (s._status === 'skipped') return 'caption'
  if (s._status === 'executing') return 'blue'
  if (s.type === 'create_ticket') return 'orange'
  if (s.type === 'move_ticket') return 'blue'
  return 'yellow'
}

export const StandupActionCard = {
  flow: 'x',
  gap: 'Z1',
  align: 'center left',
  padding: 'Z1 A',
  round: 'Z1',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: (el, s) => `${_actionColor(s)}.2`,
  background: (el, s) => `${_actionColor(s)}.06`,
  opacity: (el, s) => s._status === 'skipped' ? '.5' : '1',
  transition: 'all 200ms ease',

  Icon: {
    boxSize: 'A1',
    flexShrink: '0',
    name: (el, s) => {
      if (s._status === 'confirmed') return 'check'
      if (s.type === 'create_ticket') return 'add'
      if (s.type === 'move_ticket') return 'arrowRight'
      return 'document'
    },
    color: (el, s) => _actionColor(s)
  },

  Content: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    TicketRef: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',

      Chip: {
        hide: (el, s) => !s.number && s.type === 'create_ticket',
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'V',
        border: 'none',
        fontSize: 'Y2',
        fontWeight: '700',
        cursor: 'pointer',
        background: (el, s) => `${_actionColor(s)}.15`,
        color: (el, s) => _actionColor(s),
        ':hover': { background: (el, s) => `${_actionColor(s)}.25` },
        text: (el, s) => s.number ? `#${s.number}` : '',
        onClick: (e, el, s) => {
          e.stopPropagation()
          if (!s.number) return
          const ticket = (s.root?.myTickets || []).find(t => (t.number || t.id) === s.number)
          if (ticket) s.root?.update({ selectedTicket: ticket })
        }
      },

      TypeLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '600',
        color: (el, s) => _actionColor(s),
        text: (el, s) => {
          if (s._status === 'confirmed') return 'Applied'
          if (s._status === 'skipped') return 'Skipped'
          if (s._status === 'executing') return 'Applying…'
          if (s.type === 'create_ticket') return '+ New ticket'
          if (s.type === 'move_ticket') return 'Move'
          return 'Update'
        }
      }
    },

    Change: {
      hide: (el, s) => !s.change && !s.from,
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => {
        if (s.change) return s.change
        if (s.from && s.to) return `Status: ${s.from} → ${s.to}`
        return ''
      }
    },

    Detail: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      lineHeight: '1.4',
      text: (el, s) => {
        if (s.type === 'create_ticket') return s.title || 'New ticket'
        return s.summary || s.body || ''
      }
    }
  },

  Actions: {
    hide: (el, s) => s._status !== 'pending',
    flow: 'x',
    gap: 'Z',
    flexShrink: '0',

    ConfirmBtn: {
      tag: 'button',
      type: 'button',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Z1',
      round: 'Z',
      border: 'none',
      cursor: 'pointer',
      background: 'blue.15',
      color: 'blue',
      text: 'Confirm',
      ':hover': { background: 'blue.25' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        _confirmAction(s._id, s.root)
      }
    },

    SkipBtn: {
      tag: 'button',
      type: 'button',
      fontSize: 'Y2',
      fontWeight: '600',
      padding: 'V Z1',
      round: 'Z',
      border: 'none',
      cursor: 'pointer',
      background: 'blue.06',
      color: 'blue+50',
      text: 'Skip',
      ':hover': { color: 'blue', background: 'blue.12' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        _skipAction(s._id, s.root)
      }
    }
  },

  ResultBadge: {
    hide: (el, s) => s._status !== 'confirmed',
    tag: 'span',
    fontSize: 'Y2',
    color: 'green',
    fontWeight: '600',
    flexShrink: '0',
    text: (el, s) => s._result || 'Done'
  }
}

export const StandupQuickFill = {
  tag: 'button',
  type: 'button',
  padding: 'Y2 Z1',
  round: 'C',
  fontSize: 'Y2',
  fontWeight: '600',
  border: 'none',
  cursor: 'pointer',
  background: 'blue.12',
  color: 'blue',
  ':hover': { background: 'blue.2' },
  transition: 'background 100ms ease',
  text: (el, s) => s.label || '',
  onClick: (e, el, s) => {
    e.stopPropagation()
    const ta = el.parent?.parent?.InputBox?.Textarea?.node
    if (!ta) return
    const next = (ta.value || '') + s._text
    ta.value = next
    ta.focus()
    ta.setSelectionRange(next.length, next.length)
  }
}

export const StandupAppliedCard = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Z Z1',
  round: 'Z',
  background: 'white.04',
  transition: 'background 100ms ease',

  StatusIcon: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'green',
    text: '✓',
    flexShrink: '0'
  },

  TicketChip: {
    hide: (el, s) => !s._number && !s._title,
    tag: 'button',
    type: 'button',
    padding: 'V Z',
    round: 'V',
    border: 'none',
    fontSize: 'Y2',
    fontWeight: '700',
    flexShrink: '0',
    cursor: (el, s) => s._ticketId ? 'pointer' : 'default',
    background: (el, s) =>
      s._type === 'create_ticket' ? 'orange.15' : s._type === 'move_ticket' ? 'blue.15' : 'yellow.15',
    color: (el, s) =>
      s._type === 'create_ticket' ? 'orange' : s._type === 'move_ticket' ? 'blue' : 'yellow',
    ':hover': {
      background: (el, s) =>
        s._type === 'create_ticket' ? 'orange.25' : s._type === 'move_ticket' ? 'blue.25' : 'yellow.25'
    },
    text: (el, s) => {
      if (s._number) return `#${s._number}`
      if (s._title) return s._title
      return ''
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (!s._ticketId) return
      const ticket = (s.root?.myTickets || []).find(t => t.id === s._ticketId)
      if (ticket) s.root?.update({ selectedTicket: ticket })
    }
  },

  Detail: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    flex: '1',
    text: (el, s) => s._result || ''
  }
}

export const StandupFollowUp = {
  tag: 'button',
  type: 'button',
  padding: 'Y2 Z1',
  round: 'C',
  fontSize: 'Y2',
  fontWeight: '600',
  border: 'none',
  cursor: 'pointer',
  background: 'blue.12',
  color: 'blue',
  ':hover': { background: 'blue.2' },
  transition: 'background 100ms ease',
  text: (el, s) => s._label || '',
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s._isNew) {
      s.root?.update({
        _standupPhase: 'idle',
        _dailyDraft: '',
        _standupSummary: '',
        _standupActions: [],
        _standupFollowUps: []
      })
    } else {
      s.root?.update({
        assistantOpen: true,
        assistantMessages: [
          ...(s.root?.assistantMessages || []),
          { role: 'user', body: s._label }
        ]
      })
    }
  }
}
